import React, { useState, useEffect } from 'react';
import moment from 'moment';

import {
  PromiseToPayWithUser,
  PromiseToPaySource,
  PromiseToPayType,
  PromiseToPayReason,
  PromiseToPayCreate,
  CollectionActivityReference,
  BuyerContact,
} from '@dill/dill-shared';
import { DillModal, DillButton, DillField, DillInput, DillSelect } from '../../../../general/Dill';
import ActivityReferenceSelector from '../../../suppliers/CollectionActivityTimeline/ActivityReferenceSelector/ActivityReferenceSelector';

interface PromiseToPayFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PromiseToPayCreate) => void;
  initialData?: PromiseToPayWithUser | null;
  isLoading: boolean;
  supplierId: string;
  buyerId: string;
  contacts?: BuyerContact[];
}

const sourceOptions: { value: string; label: string }[] = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'TEXT', label: 'Text' },
  { value: 'IN_PERSON', label: 'In-Person' },
];

const typeOptions: { value: string; label: string }[] = [
  { value: 'FULL_PAYMENT', label: 'Full Payment' },
  { value: 'PARTIAL_PAYMENT', label: 'Partial Payment' },
  { value: 'PAYMENT_PLAN', label: 'Payment Plan' },
];

const reasonOptions: { value: string; label: string }[] = [
  { value: 'WAITING_ON_GC_PAYMENT', label: 'Waiting on GC Payment' },
  { value: 'CASH_FLOW_SHORTAGE', label: 'Cash Flow Shortage' },
  { value: 'MISSING_INVOICE_COPY_OR_STATEMENT', label: 'Missing Invoice Copy or Statement' },
  { value: 'NEEDS_LIEN_WAIVER', label: 'Needs Lien Waiver' },
  { value: 'RESOLVED_DISPUTE', label: 'Resolved Dispute' },
  { value: 'OTHER', label: 'Other' },
];

export const PromiseToPayFormModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading,
  supplierId,
  buyerId,
  contacts,
}: PromiseToPayFormModalProps) => {
  const [source, setSource] = useState<PromiseToPaySource | ''>('');
  const [type, setType] = useState<PromiseToPayType | ''>('');
  const [reason, setReason] = useState<PromiseToPayReason | ''>('');
  const [reasonOther, setReasonOther] = useState('');
  const [promisedPaymentDate, setPromisedPaymentDate] = useState('');
  const [amount, setAmount] = useState('');
  const [references, setReferences] = useState<CollectionActivityReference[]>([]);
  const [notes, setNotes] = useState('');

  const isEditMode = !!initialData;

  useEffect(() => {
    if (initialData) {
      setSource(initialData.source || '');
      setType(initialData.type || '');
      setReason(initialData.reason || '');
      setReasonOther(initialData.reasonOther || '');
      setPromisedPaymentDate(
        initialData.promisedPaymentDate
          ? moment(initialData.promisedPaymentDate).format('YYYY-MM-DD')
          : ''
      );
      setAmount(initialData.amount != null ? String(initialData.amount) : '');
      setReferences(initialData.references || []);
      setNotes(initialData?.notes || '');
    } else {
      setSource('');
      setType('');
      setReason('');
      setReasonOther('');
      setPromisedPaymentDate('');
      setAmount('');
      setReferences([]);
      setNotes('');
    }
  }, [initialData, open]);

  const handleSubmit = () => {
    if (!promisedPaymentDate || !source || !type || !reason) return;

    const data: PromiseToPayCreate = {
      supplierId,
      buyerId,
      source,
      type,
      reason,
      reasonOther: reason === 'OTHER' ? reasonOther || null : null,
      promisedPaymentDate: new Date(promisedPaymentDate),
      amount: amount ? parseFloat(amount) : null,
      references: references.length > 0 ? references : null,
      notes: notes || null,
      status: 'ACTIVE',
    };

    onSubmit(data);
  };

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Promise to Pay' : 'New Promise to Pay'}
      maxWidth="480px"
      footer={
        <div className="flex gap-2">
          <DillButton label="Cancel" onClick={onClose} variant="secondary" />
          <DillButton
            label={isEditMode ? 'Update' : 'Create'}
            onClick={handleSubmit}
            isDisabled={!promisedPaymentDate || !source || !type || !reason || isLoading}
          />
        </div>
      }>
      <DillField label="Source" required>
        <DillSelect
          value={source}
          onChange={(v) => setSource(v as PromiseToPaySource)}
          options={sourceOptions}
          placeholder="Select source"
        />
      </DillField>

      <DillField label="Type" required>
        <DillSelect
          value={type}
          onChange={(v) => setType(v as PromiseToPayType)}
          options={typeOptions}
          placeholder="Select type"
        />
      </DillField>

      <DillField label="Reason" required>
        <DillSelect
          value={reason}
          onChange={(v) => setReason(v as PromiseToPayReason)}
          options={reasonOptions}
          placeholder="Select reason"
        />
      </DillField>

      {reason === 'OTHER' && (
        <DillField label="Other Reason">
          <DillInput
            value={reasonOther}
            onChange={setReasonOther}
            placeholder="Describe the reason"
          />
        </DillField>
      )}

      <DillField label="Promised Payment Date" required>
        <input
          type="date"
          value={promisedPaymentDate}
          onChange={(e) => setPromisedPaymentDate(e.target.value)}
          className="h-9 w-full border border-gray-300 rounded-lg bg-white px-3 text-sm text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all"
        />
      </DillField>

      <DillField label="Amount">
        <DillInput value={amount} onChange={setAmount} placeholder="Enter amount" type="number" />
      </DillField>

      <ActivityReferenceSelector
        label="References (Optional)"
        buyerId={buyerId}
        value={references}
        onChange={setReferences}
        isDisabled={isLoading}
        allowedTypes={['INVOICE', 'JOB', 'CONTACT']}
        contacts={contacts}
      />

      <DillField label="Notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any notes..."
          rows={3}
          className="w-full border border-gray-300 rounded-lg bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 resize-none transition-all"
        />
      </DillField>
    </DillModal>
  );
};
